import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Maximize2,
  Minimize2,
  RotateCcw,
  Sparkles,
  X,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as Dialog from "@radix-ui/react-dialog";
import { useCoworkChat } from "@/hooks/use-cowork-chat";
import { useCopilotStore } from "@/stores/use-copilot-store";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatInputBar } from "./ChatInputBar";
import { MessageAttachments } from "./MessageAttachments";
import {
  formDraftCardOf,
  formDraftFailures,
  formDraftSetCardOf,
  resolveDraftState,
} from "./draft-state";
import { EnvelopeRenderer, envelopeCardOf, renderCard } from "@/components/generative-ui/envelope";
import type { FormDraftCardEnvelope, FormDraftSetCardEnvelope } from "@/components/generative-ui/envelope/types";
import { ToolFeedback, ToolResult } from "./tool-renderers";
import { toolStreamingLabel } from "./tool-labels";
import { SENDABLE_QUICK_ACTIONS } from "./quick-actions";
import {
  filePartsOf,
  formatDayLabel,
  formatMessageTime,
  isSameDay,
  messageDate,
  hasRenderableAnswer,
  hasToolResult,
  isToolResultState,
  isToolRunningState,
  partsOf,
  pausedReasonOf,
  partialContextRefTypesOf,
  textOfPart,
  toolPartsOf,
  type MessageLike,
} from "./message-parts";
import { PausedNotice } from "@/components/generative-ui/shared/PausedNotice";
import { PartialContextNotice } from "@/components/generative-ui/shared/PartialContextNotice";
import { AiKeyMissingNotice } from "@/components/ai/AiKeyMissingNotice";
import { TtsButton } from "@/components/ai/TtsButton";
import { OwnerFormDraft } from "./OwnerFormDraft";
import { KnowledgeNotice } from "./KnowledgeNotice";
import { knowledgeOf } from "./knowledge-parts";
import { PANEL_COPY } from "./panel-copy";
import { cn } from "@/lib/utils";
import { DROP_COPY } from "@/lib/drop-files";
import { installWindowDropGuard } from "@/lib/window-drop-guard";
import { LazyRouteBoundary } from "@/routes/lazy-route-boundary";
import { lazyWithRetry } from "@/routes/lazy-with-retry";

// The set card is its own chunk (WEB-FORMSET-01): a static import would pull
// the form-set controller and view into the drawer's bundle.
const OwnerFormSetDraft = lazyWithRetry(() => import("./OwnerFormSetDraft"), "OwnerFormSetDraft");

/** What the launcher needs to know while the drawer is closed. */
export type CopilotActivity = {
  streaming: boolean;
  unread: boolean;
};

type CoworkDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** Reported on every change so the floating launcher can show a streaming or
   * unread mark while the drawer is closed. */
  onActivityChange?: (activity: CopilotActivity) => void;
};

type ConversationStatus = "ready" | "streaming" | "unavailable" | "unknown";

const STATUS_LABEL: Record<ConversationStatus, string> = {
  ready: "Aguardando pedido",
  unknown: "Recursos não confirmados",
  streaming: "Respondendo…",
  unavailable: "Indisponível",
};

const STATUS_DOT: Record<ConversationStatus, string> = {
  ready: "bg-app-success",
  streaming: "bg-app-info motion-safe:animate-pulse",
  unavailable: "bg-app-danger",
  unknown: "bg-app-warning",
};

const STATUS_TEXT: Record<ConversationStatus, string> = {
  ready: "text-app-fg-muted",
  streaming: "text-app-fg",
  unavailable: "text-app-danger",
  unknown: "text-app-fg",
};

const iconButtonClass =
  "flex size-11 sm:size-10 items-center justify-center rounded-lg text-app-fg-muted transition-colors hover:bg-app-surface-muted hover:text-app-fg-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1";

/** What the clinic reads while a tool runs: the work, never the tool name. */
function ToolStreamingLabel({ toolName }: { toolName: string }) {
  return (
    <p
      role="status"
      data-testid="tool-streaming-label"
      className="inline-flex items-center gap-2 rounded-full bg-brand-soft px-3 py-1.5 text-[13px] font-medium text-brand"
    >
      <span
        aria-hidden="true"
        className="size-1.5 rounded-full bg-brand motion-safe:animate-pulse"
      />
      {toolStreamingLabel(toolName)}
    </p>
  );
}

function DaySeparator({ label }: { label: string }) {
  return (
    <p className="py-1 text-center font-display text-[11px] uppercase tracking-[0.12em] text-app-fg-muted">
      {label}
    </p>
  );
}

function QuickActionChips({ onSelect }: { onSelect: (prompt: string) => void }) {
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {SENDABLE_QUICK_ACTIONS.map((action) => (
        <button
          key={action.id}
          type="button"
          onClick={() => onSelect(action.prompt)}
          className="min-h-11 sm:min-h-10 rounded-full border border-accent-warm bg-app-surface px-3.5 py-2 text-xs font-medium text-app-fg transition-colors hover:bg-brand-soft hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

function AssistantMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-lg bg-petrol text-white",
        className
      )}
    >
      <Sparkles className="size-3.5" />
    </span>
  );
}

export function CoworkDrawer({ open, onClose, onActivityChange }: CoworkDrawerProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const prefillSentRef = useRef(false);
  // An approval replaces the tool part's output with the decision frame, so
  // the envelope that carried the form card is gone by the time the decided
  // state renders. The frame keeps a copy of the card (SCRUM-283), which is
  // what survives a remount; this map is the same card while it is open.
  const formCardsRef = useRef(new Map<string, FormDraftCardEnvelope>());
  // The set card, keyed the same way, so a decision-free remount keeps it.
  const setCardsRef = useRef(new Map<string, FormDraftSetCardEnvelope>());

  const { prefillMessage, clearPrefill, isMaximized, toggleMaximize } = useCopilotStore();

  const chat = useCoworkChat();
  const {
    aiMessages,
    isStreaming,
    inputText,
    setInputText,
    handleSubmitWithFiles,
    handleSuggestionClick,
    attachedFiles,
    setAttachedFiles,
    copilotUnavailableMessage,
    copilotUnavailableReason,
    aiKeyMissing,
    mediaRejectionMessage,
    streamErrorMessage,
    uploadingStatus,
    capabilities,
    retryCopilot,
  } = chat;

  const messages = aiMessages as MessageLike[];
  // Which approved form drafts the runtime refused to write, keyed by the
  // draft's toolCallId. Derived from the thread, so it survives a remount.
  const draftFailures = useMemo(() => formDraftFailures(messages), [messages]);
  // Only the newest set card in the thread is saveable; an older unsent one
  // shows "Substituído por um rascunho mais recente." Thread order decides,
  // and only cards the draft branch renders count.
  const latestSetCardId = useMemo(() => {
    let latest: string | null = null;
    for (const message of messages) {
      for (const tool of toolPartsOf(message)) {
        if (!tool.toolName.startsWith("draft_")) continue;
        const card = formDraftSetCardOf(tool.output);
        if (card) latest = card.card_id;
      }
    }
    return latest;
  }, [messages]);
  const copilotUnavailable = copilotUnavailableMessage !== null;
  // TENANT-AIKEY-REQUIRED-01: the stored state (no key) or a refused turn shows
  // one notice, never next to the outage banner.
  const keyRefused = aiKeyMissing === true || copilotUnavailableReason === "ai_key_missing";
  const retryUnavailable =
    copilotUnavailableReason !== "not_configured" &&
    copilotUnavailableReason !== "missing_user_token";
  const unavailableGuidance =
    copilotUnavailableReason === "not_configured"
      ? PANEL_COPY.notConfigured
      : copilotUnavailableReason === "missing_user_token"
        ? PANEL_COPY.missingUserToken
        : copilotUnavailableMessage;

  // A refused attachment is an answer, not an outage: the runtime's own pt-BR
  // sentence is what the clinic reads (422/413).
  const mediaRejected = Boolean(mediaRejectionMessage);

  const status: ConversationStatus = copilotUnavailable
    ? "unavailable"
    : isStreaming
      ? "streaming"
      : chat.capabilitiesLoading || chat.capabilitiesError ? "unknown" : "ready";

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const awaitingFirstToken =
    isStreaming &&
    (lastMessage === null ||
      lastMessage.role !== "assistant" ||
      !hasRenderableAnswer(lastMessage));
  // The run finished and the assistant produced nothing renderable: the empty
  // stream the Copilot service sends when its context call fails. Never silent.
  const showNoAnswer =
    !isStreaming &&
    !copilotUnavailable &&
    !streamErrorMessage &&
    lastMessage !== null &&
    lastMessage.role === "assistant" &&
    !hasRenderableAnswer(lastMessage);

  const lastAssistantId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === "assistant") return messages[index].id ?? null;
    }
    return null;
  }, [messages]);

  const [seenAssistantId, setSeenAssistantId] = useState<string | null>(null);
  useEffect(() => {
    if (open) setSeenAssistantId(lastAssistantId);
  }, [open, lastAssistantId]);

  const unread = !open && lastAssistantId !== null && lastAssistantId !== seenAssistantId;

  useEffect(() => {
    onActivityChange?.({ streaming: isStreaming, unread });
  }, [onActivityChange, isStreaming, unread]);

  // While open, a file dropped outside the panel and every clinic upload area does not open in the browser (D5).
  useEffect(() => (open ? installWindowDropGuard() : undefined), [open]);

  // Auto-scroll on new messages; a reader who asked for reduced motion jumps.
  useEffect(() => {
    if (!open) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    messagesEndRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
  }, [open, aiMessages]);

  // Handle prefill message from store.
  useEffect(() => {
    if (open && prefillMessage && !prefillSentRef.current) {
      prefillSentRef.current = true;
      clearPrefill();
      setInputText(prefillMessage);
    }
    if (!open) {
      prefillSentRef.current = false;
    }
  }, [open, prefillMessage, clearPrefill, setInputText]);

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-app-fg-strong/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 motion-reduce:animate-none" />
        <Dialog.Content
          asChild
          aria-label="Copiloto"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            panelRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
          }}
        >
          <section
            ref={panelRef}
            tabIndex={-1}
            onDragEnter={chat.handleDragEnter}
            onDragLeave={chat.handleDragLeave}
            onDragOver={chat.handleDragOver}
            onDrop={chat.handleDrop}
            className={cn(
              "fixed z-50 flex flex-col overflow-hidden border border-app-border bg-app-surface shadow-premium-md focus-visible:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 duration-150 motion-reduce:animate-none",
              isMaximized
                ? "inset-0 sm:inset-3 sm:left-[60px] sm:rounded-2xl lg:left-[240px]"
                : "bottom-3 right-3 top-3 w-[420px] max-w-[calc(100vw-24px)] rounded-2xl"
            )}
          >
            {chat.isDragOver && (
              <div
                data-testid="copilot-drop-overlay"
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-brand bg-app-surface p-6 text-center motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
              >
                <p className="max-w-xs text-sm font-medium text-app-fg-strong">{DROP_COPY.overlay(capabilities.inputModalities)}</p>
              </div>
            )}
            <header className="flex max-h-[30%] shrink-0 items-start justify-between gap-3 overflow-y-auto border-b border-app-border px-4 py-3">
              <div className="min-w-0">
                <Dialog.Title className="text-[11px] font-semibold uppercase tracking-[0.12em] text-app-fg-muted">
                  Copiloto
                </Dialog.Title>
                <p className="mt-1 font-display text-xl font-medium leading-tight tracking-[-0.01em] text-app-fg-strong">
                  Assistente da clínica
                </p>
                <span
                  role="status"
                  className={cn(
                    "mt-2 inline-flex items-center gap-1.5 rounded-full border border-app-border px-2.5 py-1 text-[11px] font-medium",
                    STATUS_TEXT[status]
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn("size-1.5 rounded-full", STATUS_DOT[status])}
                  />
                  {STATUS_LABEL[status]}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={toggleMaximize}
                  aria-label={isMaximized ? "Minimizar o copiloto" : "Maximizar o copiloto"}
                  className={iconButtonClass}
                >
                  {isMaximized ? (
                    <Minimize2 className="size-4" aria-hidden="true" />
                  ) : (
                    <Maximize2 className="size-4" aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Fechar o copiloto"
                  className={iconButtonClass}
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              </div>
            </header>
            <div className="max-h-[20%] shrink-0 overflow-y-auto border-b border-app-border px-4 py-2 text-xs text-app-fg">
              <p>Contexto: {chat.contextLabel ?? "Sem paciente selecionado"}.</p>
              <Dialog.Description>Fechar o painel não cancela envios nem desfaz gravações confirmadas.</Dialog.Description>
            </div>

            <div
              aria-busy={isStreaming}
              className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-app-bg px-4 py-4"
            >
              {chat.capabilitiesLoading && <p role="status">Confirmando os recursos do Copiloto…</p>}
              {chat.capabilitiesError && <div role="alert" className="space-y-2 text-sm text-app-fg">
                <p>Não foi possível confirmar os recursos do Copiloto. Seu texto foi mantido.</p>
                <Button variant="outline" disabled={chat.capabilitiesFetching} onClick={chat.retryCapabilities}>Verificar recursos</Button>
              </div>}
              {keyRefused && <AiKeyMissingNotice />}
              {chat.loadingMessages && <p role="status">Carregando contexto e rascunhos…</p>}
              {chat.conversationError && <div role="alert" className="space-y-2 text-sm text-app-fg"><p>{chat.conversationError}</p><Button variant="outline" onClick={chat.retryConversation}>Carregar contexto novamente</Button></div>}
              {copilotUnavailable && !keyRefused && (
                <div
                  role="alert"
                  className="rounded-xl border border-app-danger/30 bg-app-danger/10 p-4 text-left"
                >
                  <p className="flex items-center gap-2 text-sm font-semibold text-app-fg-strong">
                    <AlertCircle className="size-4 shrink-0 text-app-danger" aria-hidden="true" />
                    Copiloto indisponível
                  </p>
                  <p className="mt-1 text-xs leading-5 text-app-fg-muted">{unavailableGuidance}</p>
                  {retryUnavailable && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 h-11 sm:h-10"
                      onClick={retryCopilot}
                    >
                      <RotateCcw className="size-4" aria-hidden="true" />
                      Tentar novamente
                    </Button>
                  )}
                </div>
              )}

              {mediaRejected && (
                <div
                  role="alert"
                  className="rounded-xl border border-app-danger/30 bg-app-danger/10 p-4 text-left"
                >
                  <p className="flex items-center gap-2 text-sm font-semibold text-app-fg-strong">
                    <AlertCircle className="size-4 shrink-0 text-app-danger" aria-hidden="true" />
                    Anexo recusado
                  </p>
                  <p className="mt-1 text-xs leading-5 text-app-fg-muted">{mediaRejectionMessage}</p>
                </div>
              )}

              {messages.length === 0 && !copilotUnavailable && (
                <EmptyState
                  title="Como posso ajudar?"
                  description="O copiloto lê a agenda, os pacientes, o estoque e as finanças da clínica e prepara o que você aprova."
                  action={<QuickActionChips onSelect={handleSuggestionClick} />}
                />
              )}

              {messages.map((msg, index) => {
                const isUser = msg.role === "user";
                const isLast = index === messages.length - 1;
                const previous = index > 0 ? messages[index - 1] : null;
                const next = isLast ? null : messages[index + 1];
                // Consecutive turns of the same author are one group: one mark
                // at the top, one clock at the bottom.
                const startsGroup = previous === null || previous.role !== msg.role;
                const endsGroup = next === null || next.role !== msg.role;
                const timestamp = endsGroup ? formatMessageTime(msg) : null;
                const day = messageDate(msg);
                const previousDay = messageDate(previous);
                const showDay =
                  day !== null && (previousDay === null || !isSameDay(day, previousDay));
                const toolParts = toolPartsOf(msg);
                // A set card needs the whole feed width (two element groups
                // side by side with their chips); a bubble would squeeze it.
                const holdsSetCard =
                  !isUser &&
                  toolParts.some(
                    (tool) =>
                      tool.toolName.startsWith("draft_") &&
                      (formDraftSetCardOf(tool.output) !== null ||
                        setCardsRef.current.has(tool.toolCallId)),
                  );
                const withToolResults = !isUser && hasToolResult(msg);
                // A message that also carries tool cards keeps only its short
                // commentary; the cards already say the rest.
                const visibleTexts = partsOf(msg)
                  .map((part) => textOfPart(part))
                  .filter((text) => text.length > 0)
                  .filter((text) =>
                    withToolResults ? text.trim().length > 0 && text.trim().length < 200 : true
                  );
                // SCRUM-300: a turn the assistant stopped on purpose. Read
                // from the `data-paused` part's reason code, never from the
                // prose, so rewording the sentence cannot break the notice.
                const pausedReason = isUser ? null : pausedReasonOf(msg);
                const knowledge = isUser ? null : knowledgeOf(msg);
                // SCRUM-306: the turn answered without part of the page
                // context. The frame names the ref types and nothing else.
                const partialContextRefTypes = isUser ? null : partialContextRefTypesOf(msg);

                return (
                  <div key={msg.id || index} className={cn(!startsGroup && "-mt-2")}>
                    {showDay && day && <DaySeparator label={formatDayLabel(day)} />}
                    <div className={cn("flex gap-3", isUser && "justify-end")}>
                      {!isUser &&
                        (startsGroup ? (
                          <AssistantMark className="mt-0.5" />
                        ) : (
                          <span aria-hidden="true" className="size-7 shrink-0" />
                        ))}
                      <div
                        data-message-width={holdsSetCard ? "full" : "bubble"}
                        className={cn(
                          holdsSetCard ? "w-full min-w-0 max-w-full" : "max-w-[88%] min-w-0",
                          isUser && "order-1",
                        )}
                      >
                        {isUser && <MessageAttachments files={filePartsOf(msg)} />}
                        {toolParts.map((tool) => {
                          // A draft renders as the editable form when — and only
                          // when — it carries a `form.draft` or `form.draft.set` card. Any other
                          // envelope falls through to the generic renderers
                          // below; there is no legacy draft card left.
                          if (tool.toolName.startsWith("draft_")) {
                            if (isToolRunningState(tool.state)) {
                              return (
                                <div key={tool.toolCallId} className="mb-3">
                                  <ToolStreamingLabel toolName={tool.toolName} />
                                </div>
                              );
                            }

                            const parsedSet = formDraftSetCardOf(tool.output);
                            if (parsedSet) setCardsRef.current.set(tool.toolCallId, parsedSet);
                            const setCard =
                              parsedSet ?? setCardsRef.current.get(tool.toolCallId) ?? null;
                            if (setCard) {
                              const { payload: setPayload } =
                                resolveDraftState(tool.toolName, tool.input, tool.output);
                              return (
                                <div key={tool.toolCallId} className="mb-3">
                                  {/* A chunk that fails twice, or a render error in the card,
                                      stays in this slot: the clinic shell, its URL, form and
                                      scroll, and the composer keep working. */}
                                  <LazyRouteBoundary todayAction={false}>
                                    <OwnerFormSetDraft
                                      key={setCard.card_id}
                                      card={setCard}
                                      data={setPayload}
                                      superseded={setCard.card_id !== latestSetCardId}
                                    />
                                  </LazyRouteBoundary>
                                </div>
                              );
                            }

                            const parsedCard = formDraftCardOf(tool.output);
                            if (parsedCard) formCardsRef.current.set(tool.toolCallId, parsedCard);
                            const formCard =
                              parsedCard ?? formCardsRef.current.get(tool.toolCallId) ?? null;

                            if (formCard) {
                              const { payload: draftPayload, status: draftStatus } =
                                resolveDraftState(tool.toolName, tool.input, tool.output);
                              return <OwnerFormDraft key={formCard.card_id} card={formCard} data={draftPayload} previouslySubmitted={draftStatus === "approved"} />;
                            }
                          }

                          // The refusal of a confirm is already on the card the
                          // clinic approved; repeating it as a tool result
                          // would say the same thing twice.
                          if (draftFailures.handledConfirms.has(tool.toolCallId)) return null;

                          if (isToolResultState(tool.state) && tool.output) {
                            // Envelope first: a tool that emits a card renders
                            // through the registry. The legacy renderer is the
                            // fallback for the tools that still answer raw JSON.
                            const card = envelopeCardOf(tool.output);
                            // A `form.draft` or `form.draft.set` is interactive
                            // only through the draft branch above, which wires
                            // the save. Rendering one here would give the clinic
                            // a button that writes nothing, so it is skipped and
                            // reported instead.
                            if (card?.kind === "form.draft" || card?.kind === "form.draft.set") {
                              if (import.meta.env.DEV) {
                                // The tool name only: never card data.
                                console.warn(
                                  "cowork: draft card outside the draft gate, not rendered",
                                  tool.toolName,
                                );
                              }
                              return null;
                            }
                            const rendered = card ? renderCard(card) : null;
                            if (card && rendered) {
                              return (
                                <div key={tool.toolCallId} className="mb-3">
                                  <EnvelopeRenderer card={card}>{rendered}</EnvelopeRenderer>
                                  <ToolFeedback toolCallId={tool.toolCallId} />
                                </div>
                              );
                            }

                            return (
                              <div key={tool.toolCallId} className="mb-3">
                                <ToolResult
                                  toolName={tool.toolName}
                                  status="done"
                                  result={
                                    typeof tool.output === "string"
                                      ? tool.output
                                      : JSON.stringify(tool.output)
                                  }
                                />
                              </div>
                            );
                          }

                          if (isToolRunningState(tool.state)) {
                            return (
                              <div key={tool.toolCallId} className="mb-3">
                                <ToolStreamingLabel toolName={tool.toolName} />
                              </div>
                            );
                          }

                          return null;
                        })}

                        {visibleTexts.map((text, textIndex) => (
                          <div
                            key={`text-${textIndex}`}
                            className={cn(
                              "text-sm leading-relaxed",
                              isUser
                                ? "rounded-2xl rounded-br-md bg-brand-soft px-4 py-2.5 text-app-fg-strong"
                                : "rounded-2xl rounded-tl-md border border-app-border bg-app-surface px-4 py-3 text-app-fg"
                            )}
                          >
                            {isUser ? (
                              <span className="font-medium">{text}</span>
                            ) : (
                              <div className="cowork-prose prose prose-sm max-w-none">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                                {isStreaming && isLast && textIndex === visibleTexts.length - 1 && (
                                  <span
                                    aria-hidden="true"
                                    data-testid="streaming-cursor"
                                    className="ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 bg-brand align-middle motion-safe:animate-pulse"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        ))}

                        {pausedReason && <PausedNotice reasonCode={pausedReason} />}
                        {knowledge && <KnowledgeNotice knowledge={knowledge} />}

                        {partialContextRefTypes && (
                          <PartialContextNotice refTypes={partialContextRefTypes} />
                        )}

                        {!isUser && (() => {
                          const ttsText = partsOf(msg)
                            .map((part) => textOfPart(part))
                            .join(" ")
                            .trim();
                          if (!ttsText) return null;
                          return (
                            <div className="mt-1 flex items-center">
                              <TtsButton content={ttsText} />
                            </div>
                          );
                        })()}

                        {timestamp && (
                          <p
                            className={cn(
                              "mt-1 text-[11px] tabular-nums text-app-fg-subtle",
                              isUser && "text-right"
                            )}
                          >
                            {timestamp}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {awaitingFirstToken && (
                <div className="flex gap-3" data-testid="copilot-thinking">
                  <AssistantMark />
                  <div className="w-52 rounded-2xl rounded-tl-md border border-app-border bg-app-surface px-4 py-3">
                    <Skeleton className="h-2.5 w-40 rounded-full motion-reduce:animate-none" />
                    <Skeleton className="mt-2 h-2.5 w-24 rounded-full motion-reduce:animate-none" />
                  </div>
                </div>
              )}

              {streamErrorMessage && (
                <div className="flex gap-3" data-testid="copilot-stream-error">
                  <AssistantMark className="mt-0.5" />
                  <div
                    role="alert"
                    className="max-w-[88%] rounded-2xl rounded-tl-md border border-app-danger/30 bg-app-danger/10 px-4 py-3"
                  >
                    <p className="text-sm leading-relaxed text-app-fg-strong">
                      {streamErrorMessage}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 h-11 sm:h-10"
                      onClick={retryCopilot}
                    >
                      <RotateCcw className="size-4" aria-hidden="true" />
                      Tentar novamente
                    </Button>
                  </div>
                </div>
              )}

              {showNoAnswer && (
                <div
                  role="alert"
                  className="rounded-xl border border-app-warning/40 bg-app-warning/10 p-4 text-left"
                >
                  <p className="flex items-center gap-2 text-sm font-semibold text-app-fg-strong">
                    <AlertTriangle className="size-4 shrink-0 text-app-warning" aria-hidden="true" />
                    Sem resposta
                  </p>
                  <p className="mt-1 text-xs leading-5 text-app-fg-muted">{PANEL_COPY.noAnswer}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3 h-11 sm:h-10"
                    onClick={retryCopilot}
                  >
                    <RotateCcw className="size-4" aria-hidden="true" />
                    Tentar novamente
                  </Button>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="max-h-[40%] shrink-0 overflow-y-auto overscroll-contain border-t border-app-border bg-app-surface px-3 pb-3 pt-2">
              <ChatInputBar
                value={inputText}
                onChange={setInputText}
                onSubmit={handleSubmitWithFiles}
                disabled={isStreaming || chat.capabilitiesLoading || chat.capabilitiesError || chat.loadingMessages || Boolean(chat.conversationError)}
                placeholder="Pergunte ao copiloto…"
                attachedFiles={attachedFiles}
                onFilesChange={setAttachedFiles}
                onFilesRefused={chat.refuseAttachments}
                capabilities={capabilities}
                isVoiceMode={chat.isVoiceMode}
                setIsVoiceMode={chat.setIsVoiceMode}
              />
              {uploadingStatus && (
                <p role="status" className="mt-1.5 px-1 text-xs text-app-fg-muted">
                  {uploadingStatus}
                </p>
              )}
              {chat.dropNotice && (
                <p role="status" className="mt-1.5 px-1 text-xs text-app-fg-strong">
                  {chat.dropNotice}
                </p>
              )}
            </div>
          </section>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
